/**
 * File utilities for handling file operations
 */

/**
 * Serializes a file to a base64 data URL string using the FileReader API
 * @param file - The File object to serialize
 * @returns Promise<string> that resolves with the file's base64 data URL string or rejects on error
 */
export async function serializeFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const result = event.target?.result;
      if (typeof result === "string") {
        resolve(result);
      } else {
        reject(new Error("Failed to read file as data URL"));
      }
    };

    reader.onerror = () => {
      reject(new Error("Error reading file"));
    };

    reader.onabort = () => {
      reject(new Error("File reading was aborted"));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Compresses an image file if it's larger than 1MB, preserving as much quality as possible
 * @param file - The File object to potentially compress (must be PNG or JPEG)
 * @returns Promise<File> that resolves with the compressed file or original if already ≤1MB
 * @throws Error if the file is not a PNG or JPEG image or if browser doesn't support required APIs
 */
export async function compressImageIfNeeded(file: File): Promise<File> {
  const MAX_SIZE = 1024 * 1024; // 1MB in bytes

  console.log("🗜️ [ImageCompression] Starting compression check for:", {
    maxSizeMB: (MAX_SIZE / (1024 * 1024)).toFixed(2),
    name: file.name,
    size: file.size,
    sizeMB: (file.size / (1024 * 1024)).toFixed(2),
    type: file.type,
  });

  // Check browser support for required APIs
  if (
    typeof window === "undefined" ||
    !window.FileReader ||
    !window.Image ||
    !document.createElement
  ) {
    const error =
      "Browser environment does not support required APIs for image compression";
    console.error("❌ [ImageCompression] Browser support check failed:", error);
    throw new Error(error);
  }

  // Check if file is PNG or JPEG (also handle 'image/jpg' variant)
  if (!file.type.match(/^image\/(png|jpeg|jpg)$/i)) {
    const error = "File must be a PNG or JPEG image";
    console.error("❌ [ImageCompression] File type check failed:", {
      error,
      type: file.type,
    });
    throw new Error(error);
  }

  console.log(
    "✅ [ImageCompression] Browser support and file type checks passed",
  );

  // Return original file if already ≤1MB
  if (file.size <= MAX_SIZE) {
    console.log("⏭️ [ImageCompression] File already ≤1MB, returning original");
    return file;
  }

  console.log(
    "🔄 [ImageCompression] File >1MB, starting compression process...",
  );

  return new Promise((resolve, reject) => {
    // Set timeout to prevent hanging
    const timeoutId = setTimeout(() => {
      reject(new Error("Image compression timed out"));
    }, 30000); // 30 second timeout

    const cleanup = () => {
      clearTimeout(timeoutId);
    };

    const img = new Image();
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      cleanup();
      reject(
        new Error(
          "Failed to get 2D canvas context - canvas may not be supported",
        ),
      );
      return;
    }

    // Check for canvas.toBlob support
    if (!canvas.toBlob) {
      cleanup();
      reject(new Error("Browser does not support canvas.toBlob method"));
      return;
    }

    img.onload = () => {
      try {
        console.log(
          `📏 [ImageCompression] Image loaded successfully: ${img.width}x${img.height}`,
        );

        // Validate image dimensions
        if (img.width === 0 || img.height === 0) {
          console.error("❌ [ImageCompression] Invalid image dimensions");
          cleanup();
          reject(new Error("Invalid image dimensions"));
          return;
        }

        // Calculate scale factor to reduce file size
        // Start with a conservative scale reduction
        let scaleFactor = Math.sqrt(MAX_SIZE / file.size);

        console.log(
          `📐 [ImageCompression] Initial scale factor: ${scaleFactor.toFixed(3)} (based on file size ratio)`,
        );

        // Ensure minimum reasonable dimensions (at least 100px on smallest side)
        const minDimension = 100;
        const minScaleForWidth = minDimension / img.width;
        const minScaleForHeight = minDimension / img.height;
        const minScale = Math.max(minScaleForWidth, minScaleForHeight);
        scaleFactor = Math.max(scaleFactor, minScale, 0.1);

        console.log(
          `📐 [ImageCompression] Final scale factor: ${scaleFactor.toFixed(3)} (with minimum constraints)`,
        );

        // Set canvas dimensions
        const newWidth = Math.max(1, Math.floor(img.width * scaleFactor));
        const newHeight = Math.max(1, Math.floor(img.height * scaleFactor));
        canvas.width = newWidth;
        canvas.height = newHeight;

        console.log(
          `🖼️ [ImageCompression] Canvas resized to: ${newWidth}x${newHeight} (from ${img.width}x${img.height})`,
        );

        // Clear canvas and set high quality rendering
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        console.log("🎨 [ImageCompression] Drawing scaled image to canvas...");
        // Draw scaled image
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Try different quality levels to achieve target size
        const tryCompress = (quality: number): void => {
          const outputType =
            file.type === "image/png" ? "image/png" : "image/jpeg";

          console.log(
            `🎯 [ImageCompression] Trying compression with quality: ${quality.toFixed(2)}, type: ${outputType}`,
          );

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                console.error("❌ [ImageCompression] toBlob returned null");
                cleanup();
                reject(
                  new Error("Failed to compress image - toBlob returned null"),
                );
                return;
              }

              console.log(
                `📊 [ImageCompression] Compressed blob size: ${blob.size} bytes (${(blob.size / (1024 * 1024)).toFixed(2)}MB), target: ${MAX_SIZE} bytes`,
              );

              // If we achieved target size or quality is too low, use this result
              // Add error marging of .5 MB to avoid reducing quality too much
              if (blob.size <= MAX_SIZE + 512 * 1024 || quality <= 0.5) {
                console.log(
                  `✅ [ImageCompression] Compression successful! Final size: ${blob.size} bytes, quality: ${quality.toFixed(2)}`,
                );
                try {
                  const compressedFile = new File([blob], file.name, {
                    lastModified: file.lastModified,
                    type: outputType,
                  });
                  cleanup();
                  resolve(compressedFile);
                } catch (error) {
                  console.error(
                    "❌ [ImageCompression] Failed to create File object:",
                    error,
                  );
                  cleanup();
                  reject(
                    new Error(`Failed to create compressed file: ${error}`),
                  );
                }
              } else {
                // Try with lower quality (smaller steps for better control)
                const nextQuality = Math.max(0.1, quality - 0.05);
                console.log(
                  `🔄 [ImageCompression] Size still too large, trying lower quality: ${nextQuality.toFixed(2)}`,
                );
                tryCompress(nextQuality);
              }
            },
            outputType,
            quality,
          );
        };

        console.log(
          "🎨 [ImageCompression] Starting compression with initial quality 0.92",
        );
        // Start with high quality and work down
        tryCompress(0.92);
      } catch (error) {
        cleanup();
        reject(new Error(`Error during image processing: ${error}`));
      }
    };

    img.onerror = (event) => {
      cleanup();
      reject(
        new Error("Failed to load image - file may be corrupted or invalid"),
      );
    };

    img.onabort = () => {
      cleanup();
      reject(new Error("Image loading was aborted"));
    };

    // Load the image using FileReader
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result && typeof e.target.result === "string") {
        img.src = e.target.result;
      } else {
        cleanup();
        reject(new Error("Failed to read file as data URL"));
      }
    };

    reader.onerror = () => {
      cleanup();
      reject(new Error("FileReader error - failed to read file"));
    };

    reader.onabort = () => {
      cleanup();
      reject(new Error("File reading was aborted"));
    };

    // Start reading the file
    try {
      reader.readAsDataURL(file);
    } catch (error) {
      cleanup();
      reject(new Error(`Failed to start reading file: ${error}`));
    }
  });
}
